
import * as pdfjs from 'pdfjs-dist';
import { initializePdfJs, isPdfWorkerAvailable } from '../pdfJsConfig';

/**
 * Extract text from a PDF file
 */
export async function extractPDFText(file: File, onProgress?: (progress: number) => void): Promise<string> {
  console.log("Processando PDF:", file.name);
  
  try {
    // **CENTRALIZAÇÃO**: Aguarda Promise única do PDF.js
    await initializePdfJs();
    
    const arrayBuffer = await file.arrayBuffer();
    console.log("Arquivo convertido para ArrayBuffer, tamanho:", arrayBuffer.byteLength);
    
    if (onProgress) onProgress(20);
    
    // Get the PDF document
    const pdfDocument = await pdfjs.getDocument({
      data: arrayBuffer,
      // worker: null removido - usando Module Worker centralizado
      cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
      cMapPacked: true,
      standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/standard_fonts/',
    }).promise;
    
    console.log("PDF carregado com sucesso. Total de páginas:", pdfDocument.numPages);
    
    if (onProgress) onProgress(30);
    
    const MAX_CHARS = 1000000;
    let combinedText = '';
    let extractedPages = 0;
    
    // Process pages
    for (let i = 1; i <= pdfDocument.numPages && combinedText.length < MAX_CHARS; i++) {
      try {
        const page = await pdfDocument.getPage(i);
        console.log(`Extraindo texto da página ${i}...`);
        
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => 'str' in item ? item.str : '')
          .join(' ');
          
        combinedText += pageText + '\n\n';
        extractedPages++;
        
        // Update progress more frequently
        if (onProgress) {
          const progressPct = 30 + ((i / pdfDocument.numPages) * 60);
          onProgress(Math.min(90, Math.round(progressPct)));
        }
        
        console.log(`Página ${i}: Extraídos ${pageText.length} caracteres`);
      } catch (pageError) {
        console.error(`Falha ao extrair a página ${i}:`, pageError);
      }
    }
    
    if (onProgress) onProgress(100);
    
    if (combinedText.length > 0) {
      return combinedText.trim();
    } else {
      return generateFallbackText(file);
    }
  } catch (error) {
    console.error("Erro ao extrair texto do PDF:", error);
    if (onProgress) onProgress(100);
    return generateFallbackText(file);
  }
}

/**
 * Generate fallback text when extraction fails
 */
function generateFallbackText(file: File): string {
  return `[PDF: ${file.name}]

Não foi possível extrair o texto completo do PDF. 
      
O documento possivelmente utiliza técnicas de proteção ou contém imagens escaneadas com texto não selecionável.

Análise do nome do arquivo: ${file.name}
Tamanho: ${(file.size / 1024 / 1024).toFixed(2)} MB

Para uma análise completa, recomendamos fornecer detalhes adicionais sobre este documento.`;
}

/**
 * Extract text from a text file
 */
export async function extractTextFromTextFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      try {
        const text = reader.result as string;
        resolve(text);
      } catch (e) {
        reject(e);
      }
    };
    
    reader.onerror = (error) => {
      reject(error);
    };
    
    reader.readAsText(file);
  });
}

/**
 * Extract text from PDF using alternative method
 */
export async function extractPDFTextAlternative(file: File, onProgress?: (progress: number) => void): Promise<string> {
  console.log("Tentando método alternativo de extração para PDF:", file.name);
  
  try {
    // **CENTRALIZAÇÃO**: Aguarda Promise única do PDF.js
    await initializePdfJs();
    
    if (onProgress) onProgress(15);
    
    const fileURL = URL.createObjectURL(file);
    const pdfDocument = await pdfjs.getDocument({
      url: fileURL,
      // worker: null removido - usando Module Worker centralizado
      cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
      cMapPacked: true,
    }).promise;
    
    if (onProgress) onProgress(30);
    
    console.log("Método alternativo: PDF carregado com sucesso. Total de páginas:", pdfDocument.numPages);
    
    let combinedText = '';
    const pageCount = Math.min(pdfDocument.numPages, 50);
    
    for (let i = 1; i <= pageCount; i++) {
      try {
        const page = await pdfDocument.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => 'str' in item ? item.str : '')
          .join(' ');
          
        combinedText += pageText + '\n\n';
        
        if (onProgress) {
          const progressPct = 30 + ((i / pageCount) * 60);
          onProgress(Math.min(90, Math.round(progressPct)));
        }
      } catch (pageError) {
        console.error(`Falha ao extrair a página ${i} (método alternativo):`, pageError);
      }
    }
    
    URL.revokeObjectURL(fileURL);
    if (onProgress) onProgress(100);
    
    return combinedText.trim() || generateFallbackText(file);
  } catch (error) {
    console.error("Erro no método alternativo de extração:", error);
    if (onProgress) onProgress(100);
    return generateFallbackText(file);
  }
}
